package top.wcpe.allincore.dsl.compiler.api

import top.wcpe.allincore.dsl.compiler.type.IrType

/**
 * 事件类型字段表（M9 阶段 4 引入）。
 *
 * 把 lambda 形参的事件类型 → 字段集合的映射集中到 SSOT。让 IDE 在 lambda body 内
 * 输入 `event.<TAB>` 时按 prop 名 / await 形态查表补出字段候选。
 *
 * **范围限定**（plan §7.1）：
 * - MouseEvent：onClick / onPress / onRelease / onHover / onLeave 等鼠标事件
 * - KeyEvent：onKeyDown / onKeyUp 等键盘事件
 * - ChangeEvent：onChange 等值变化事件
 * - NetSubscribeEvent：subscribe { ns { topic { handler = lambda } } } 内部
 * - NetRequestResult：await net.request(...) { result -> ... } 内部
 *
 * **不在本表范围**：
 * - 自定义事件类型（用户定义的 data class）：不属于 IDE 元数据驱动的范围
 * - lambda 形参的复合类型推断（如 `forEach { item -> }` 中的 item 类型）：留待 M10
 *
 * 与 [PropMetadata] / [TypeMembers] / [ContextMetadata] 一样作为编译器对 IDE 的 SSOT；
 * 新增 / 修改时仅在本处更新即可。
 */
object EventTypeMetadata {

    /** 事件类型分类。 */
    enum class EventType { Mouse, Key, Change, NetSubscribe, NetRequestResult }

    /**
     * 单个事件字段的元数据。
     *
     * @property name 字段名（lambda body 内 `event.X` 的 X）
     * @property type 字段的运行时类型，用于 IDE 链式补全
     * @property doc 简短中文文档，用于 hover / 补全 typeText
     */
    data class EventField(
        val name: String,
        val type: IrType,
        val doc: String,
    )

    /** 事件类型 → 字段集合表。 */
    val fieldsByType: Map<EventType, List<EventField>> = mapOf(
        EventType.Mouse to listOf(
            EventField("x", IrType.NumberT, "鼠标 X 坐标（相对元素左上角）"),
            EventField("y", IrType.NumberT, "鼠标 Y 坐标"),
            EventField("button", IrType.StringT, "按键名（left / right / middle）"),
            EventField("shift", IrType.BooleanT, "是否按下 Shift"),
            EventField("ctrl", IrType.BooleanT, "是否按下 Ctrl"),
            EventField("alt", IrType.BooleanT, "是否按下 Alt"),
            EventField("meta", IrType.BooleanT, "是否按下 Meta（Win / Cmd）"),
        ),
        EventType.Key to listOf(
            EventField("key", IrType.StringT, "按键字符（如 \"a\" / \"Enter\"）"),
            EventField("code", IrType.StringT, "按键码（如 \"KeyA\" / \"Enter\"）"),
            EventField("shift", IrType.BooleanT, "是否按下 Shift"),
            EventField("ctrl", IrType.BooleanT, "是否按下 Ctrl"),
            EventField("alt", IrType.BooleanT, "是否按下 Alt"),
            EventField("meta", IrType.BooleanT, "是否按下 Meta"),
        ),
        EventType.Change to listOf(
            EventField("oldValue", IrType.AnyT, "变化前的值"),
            EventField("newValue", IrType.AnyT, "变化后的值"),
        ),
        EventType.NetSubscribe to listOf(
            EventField("namespace", IrType.StringT, "事件命名空间"),
            EventField("topic", IrType.StringT, "事件 topic"),
            EventField("payload", IrType.MapT(), "事件 payload（typed helper 取值）"),
        ),
        EventType.NetRequestResult to listOf(
            EventField("success", IrType.BooleanT, "请求是否成功"),
            EventField("payload", IrType.MapT(), "成功时的响应 payload"),
            EventField("error", IrType.StringT, "失败时的错误描述"),
            EventField("code", IrType.NumberT, "状态码（HTTP 风格）"),
        ),
    )

    /**
     * 按 prop 名决定 lambda 形参的事件类型。
     *
     * 命中范围与 PropMetadata 已知 prop 一致；新增鼠标 / 键盘相关 prop 时同步更新。
     * 返回 null 表示该 prop 不绑定 IDE 已识别的事件类型，让调用方退回通用补全。
     */
    fun resolveEventTypeFromProp(propName: String): EventType? = when (propName) {
        "onClick", "onPress", "onRelease", "onHover", "onLeave",
        "onMouseDown", "onMouseUp", "onMouseMove" -> EventType.Mouse
        "onKeyDown", "onKeyUp", "onKeyPress" -> EventType.Key
        "onChange", "onInput", "onSelect" -> EventType.Change
        else -> null
    }

    /** 列出某事件类型的全部字段；未登记类型返回空列表。 */
    fun getFields(type: EventType): List<EventField> = fieldsByType[type].orEmpty()

    /** 列出全部事件字段名（去重 + 用于诊断与调试）。 */
    fun allFieldNames(): Set<String> = fieldsByType.values
        .flatMap { fields -> fields.map { it.name } }
        .toSet()

    /**
     * Lambda 形参类型表（M10 Stage 4 引入）。
     *
     * prop 名 → lambda 形参的预期类型列表。当 lambda 处于 `propName = { params -> ... }`
     * 且 prop 名命中本表时，TypeConstraintChecker 用此回填形参类型，避免误报 E663。
     *
     * 当前覆盖事件类 prop（与 [resolveEventTypeFromProp] 对齐）；其它接受 lambda 的 prop
     * 由 [top.wcpe.allincore.dsl.compiler.semantic.LambdaParamChecker] 在表外报 E663
     * 提醒用户显式标注。
     *
     * **同步性约束**：与 [resolveEventTypeFromProp] 共享同一组 prop 字面量
     * （mouseProps / keyProps / changeProps）；维护时两处必须同步——后续若新增
     * 鼠标 / 键盘 / 变化事件 prop，请同时更新两表，避免一处认得新 prop 而另一处不认。
     */
    private val LAMBDA_PARAM_TYPES_BY_PROP: Map<String, List<IrType>> = run {
        // 鼠标 / 键盘 / 变化事件 prop 的 lambda 形参都是单参 AnyT
        // —— 第一版不下钻具体事件子类型（如 MouseEvent vs KeyEvent），保持简单
        val singleAny = listOf<IrType>(IrType.AnyT)
        val mouseProps = listOf(
            "onClick", "onPress", "onRelease", "onHover", "onLeave",
            "onMouseDown", "onMouseUp", "onMouseMove",
        )
        val keyProps = listOf("onKeyDown", "onKeyUp", "onKeyPress")
        val changeProps = listOf("onChange", "onInput", "onSelect")
        val map = mutableMapOf<String, List<IrType>>()
        (mouseProps + keyProps + changeProps).forEach { map[it] = singleAny }
        map
    }

    /**
     * 查 prop 对应的 lambda 形参类型列表。
     *
     * 命中：返回形参类型列表（与 lambda 形参数量对齐）；
     * 未命中：返回 null，调用方可据此触发 E663 INFO。
     */
    fun resolveLambdaParamTypes(propName: String): List<IrType>? = LAMBDA_PARAM_TYPES_BY_PROP[propName]
}
